/**
 * voice-v8 — Läuft die Tonverarbeitung der Seite wirklich? (26.09.2026, Ticket 2634)
 *
 * ⚠️⚠️ DER KONTEXT SAGT „running" UND RECHNET TROTZDEM NICHT.
 *
 * Martin, 26.09. 07:14: *„Die Aufnahme läuft, aber die Transkription bleibt
 * hängen und der Visualizer zeigt nichts — ein immer wiederkehrendes
 * Problem."* Dazu griff das Absenden nach 2 s Stille nicht.
 *
 * Das Protokoll 22.–26.09. (alle Drücke ab 3 s):
 *   - 68 Drücke mit Pegel exakt 0/0/0 (`[ptt-stille]`), bei laufendem
 *     Kontext (`ptt-wache-null … ctx=running ctxLief=ja`).
 *   - Bei 61 davon schickte das Live-Diktat 0 KB. Sein ScriptProcessor ruft
 *     `onaudioprocess` nur, wenn der Kontext rechnet — auch reine Stille
 *     hätte Bytes ergeben. Der Kontext rechnete also gar nicht.
 *   - Die Aufnahme selbst war heil (07:14: 752 kB, 680 Zeichen erkannt).
 *   - Bei Martin beginnt es nur beim ersten Druck nach der Rückkehr aus dem
 *     Hintergrund (3 von 88) und hält an (4 von 6 Folgedrücken), bis die App
 *     wieder verdeckt war oder der Ton auf Bluetooth wechselte. Nach einem
 *     heilen Druck nie (0 von 186).
 *
 * Folge: Pegel 0 → „noch nicht gesprochen" → kein Absenden nach Stille;
 * Mitschrift leer; Balken tot. Und `state` sagt „running", darum griff das
 * bisherige Wecken (nur bei „suspended") nie.
 *
 * Was hier geschieht: die Uhr des Kontexts (`currentTime`) gegen die Wanduhr
 * halten. Läuft sie über TAKT_STEHT_AB_MS weniger als ein Viertel mit, steht
 * der Takt. Dann eine Leiter, jede Stufe genau einmal und mit Zeile im
 * Protokoll:
 *   1. anhalten und wieder starten (`suspend` → `resume`),
 *   2. einen frischen Kontext bauen lassen (der Besitzer weiß, wie),
 *   3. aufgeben — der Besitzer sagt es dem Menschen.
 * Kommt der Takt nach Stufe 1 oder 2 wieder, steht „gerettet" im Protokoll.
 *
 * ⚠️ Ob Stufe 1 oder 2 auf dem iPhone hilft, ist NICHT belegt — genau das
 *    sollen die Zeilen zeigen. Stehen danach nur „tot"-Zeilen da, liegt es
 *    tiefer (die geteilte Tonausgabe von WebKit), und dann hilft nur ein
 *    Eingriff auf der nativen Seite.
 */

import { motorAudioText, motorSitzungText } from "./wachwort-zustand.js";

/** So oft wird nachgesehen. */
export const TAKT_PRUEF_MS = 250;
/** So lange muss die Uhr stehen, bevor etwas geschieht. */
export const TAKT_STEHT_AB_MS = 1200;
/** Weniger als dieser Anteil der Wanduhr gelaufen → der Takt steht. */
export const TAKT_MIN_ANTEIL = 0.25;
/** Höchstens so viele Leitern je Wache — sonst flutet ein Wackelkontakt das Protokoll. */
export const TAKT_HOECHSTENS_LEITERN = 3;

export interface TaktProbe {
  wandMs: number;
  /** `currentTime` des Kontexts in Sekunden. */
  kontextS: number;
  /** `state === "running"` — nur dann soll die Uhr laufen. */
  laeuft: boolean;
}

/**
 * Steht der Takt zwischen zwei Proben? Nur, wenn der Kontext beide Male
 * „running" meldete: ein angehaltener Kontext steht zu Recht, den weckt
 * sein Besitzer.
 */
export function taktSteht(von: TaktProbe, bis: TaktProbe): boolean {
  if (!von.laeuft || !bis.laeuft) return false;
  const wand = bis.wandMs - von.wandMs;
  if (wand < TAKT_STEHT_AB_MS) return false;
  return (bis.kontextS - von.kontextS) * 1000 < wand * TAKT_MIN_ANTEIL;
}

type TaktStufe = "pause" | "neu" | "tot" | "gerettet";

export interface TaktEreignis {
  stufe: TaktStufe;
  /** Seit Beginn der Wache. */
  nachMs: number;
  /** Bei „gerettet": welche Stufe es war. */
  durch?: "pause" | "neu";
  /** Abtastrate des Kontexts, der gerade gilt. */
  rate: number;
}

/**
 * Der Rest der Zeile `[mikro] rechentakt-<stufe> …` — EINE Fassung für Pegel und Diktat.
 *
 * 08.10.2026 (Martin: *„Das ist auch immer wieder, dass der Pegel nicht angezeigt wird!!“*): dazu der Stand der
 * nativen Tonsitzung, wie der Mikrofon-Motor ihn zuletzt meldete (wachwort-zustand.ts). Das Journal 01.–08.10.
 * zeigte: tot war der Takt fast nur, wenn der Griff der Sprechtaste die Tonsitzung des Motors unterbrach und dabei
 * kein anderer Ton lief (`audio=nein`, 21 von 25) — oder in den Drücken danach, solange die Sitzung unterbrochen
 * blieb (30 von 44); ohne Motor nie (0 von 137). Mit `motor=` und `audio=` ist das an jeder Zeile nachzuzählen,
 * ohne die Motorzeilen daneben zu legen. `motor=unterbrochen/4.4s` an `rechentakt-tot` (4,5 s nach dem Start) heißt:
 * unterbrochen im Augenblick des Griffs; `-` = kein Motor (Browser, ältere App).
 */
export function taktZusatz(wer: "pegel" | "diktat", e: TaktEreignis): string {
  return (
    `wer=${wer} nach=${e.nachMs}ms rate=${e.rate}${e.durch ? ` durch=${e.durch}` : ""}` +
    ` motor=${motorSitzungText()} audio=${motorAudioText()}`
  );
}

/** Was die Wache vom Kontext braucht — ein echter AudioContext hat alles. */
export interface TaktKontext {
  readonly state: string;
  readonly currentTime: number;
  readonly sampleRate: number;
  suspend?: () => Promise<void>;
  resume?: () => Promise<void>;
}

interface TaktWacheOpts {
  /**
   * Stufe 2: baut einen frischen Kontext samt Graph und gibt ihn zurück —
   * oder null, wenn es nicht geht. Den alten schließt der Besitzer NICHT
   * sofort (siehe mic-visualizer.ts), das ist seine Sache.
   */
  neuBauen: () => TaktKontext | null;
  melde: (e: TaktEreignis) => void;
  jetzt?: () => number;
  /** Verdeckt rechnet iOS zu Recht nicht — dann wird nichts entschieden. */
  sichtbar?: () => boolean;
  takt?: (fn: () => void, ms: number) => unknown;
  halt?: (h: unknown) => void;
}

export interface TaktWache {
  stop(): void;
  /** Steht der Takt gerade (erkannt und noch nicht wieder gelaufen)? */
  steht(): boolean;
}

export function wacheRechentakt(start: TaktKontext, o: TaktWacheOpts): TaktWache {
  const jetzt = o.jetzt ?? (() => performance.now());
  const sichtbar =
    o.sichtbar ??
    (() => typeof document === "undefined" || document.visibilityState !== "hidden");
  const takt = o.takt ?? ((fn: () => void, ms: number) => window.setInterval(fn, ms));
  const halt = o.halt ?? ((h: unknown) => window.clearInterval(h as number));

  let kontext = start;
  const beginn = jetzt();
  let basis: TaktProbe | null = null;
  /** 0 beobachten, 1 nach „pause", 2 nach „neu", 3 aufgegeben. */
  let stufe = 0;
  /** Seit wann der Kontext in Stufe 1/2 nicht „running" meldet. */
  let nichtLaufendSeit: number | null = null;
  let stehtJetzt = false;
  let leitern = 0;
  let aus = false;

  const melde = (stufeName: TaktStufe, durch?: "pause" | "neu"): void => {
    try {
      o.melde({
        stufe: stufeName,
        nachMs: Math.round(jetzt() - beginn),
        rate: Math.round(kontext.sampleRate || 0),
        ...(durch ? { durch } : {}),
      });
    } catch {
      /* Melden ist Kür */
    }
  };

  const beende = (): void => {
    if (aus) return;
    aus = true;
    halt(handle);
  };

  /** Der Takt steht — eine Stufe weiter. */
  const eskaliere = (): void => {
    stehtJetzt = true;
    basis = null;
    nichtLaufendSeit = null;
    if (stufe === 0) {
      if (leitern >= TAKT_HOECHSTENS_LEITERN) {
        // Stumm aufgeben: der Besitzer hat „tot" schon einmal gehört oder
        // der Takt kippt ständig — beides sagt die nächste Zeile nicht klüger.
        stufe = 3;
        beende();
        return;
      }
      leitern++;
      stufe = 1;
      melde("pause");
      const k = kontext;
      void Promise.resolve()
        .then(() => k.suspend?.())
        .then(() => k.resume?.())
        .catch(() => undefined);
      return;
    }
    if (stufe === 1) {
      let neu: TaktKontext | null = null;
      try {
        neu = o.neuBauen();
      } catch {
        neu = null;
      }
      if (neu) {
        kontext = neu;
        stufe = 2;
        melde("neu");
        return;
      }
    }
    stufe = 3;
    melde("tot");
    beende();
  };

  const pruefe = (): void => {
    if (aus) return;
    if (!sichtbar()) {
      basis = null;
      nichtLaufendSeit = null;
      return;
    }
    const zeit = kontext.currentTime;
    if (typeof zeit !== "number" || !Number.isFinite(zeit)) return;
    const probe: TaktProbe = { wandMs: jetzt(), kontextS: zeit, laeuft: kontext.state === "running" };
    if (!probe.laeuft) {
      basis = null;
      // Nach einem Rettungsversuch zählt auch ein Kontext, der nicht wieder
      // anläuft, als stehend — sonst bliebe die Leiter auf Stufe 1 hängen,
      // wenn `resume` ohne Geste nicht greift.
      if (stufe === 1 || stufe === 2) {
        nichtLaufendSeit ??= probe.wandMs;
        if (probe.wandMs - nichtLaufendSeit >= TAKT_STEHT_AB_MS) eskaliere();
      }
      return;
    }
    nichtLaufendSeit = null;
    if (!basis) {
      basis = probe;
      return;
    }
    if (probe.wandMs - basis.wandMs < TAKT_STEHT_AB_MS) return;
    const steht = taktSteht(basis, probe);
    basis = probe;
    if (steht) {
      eskaliere();
      return;
    }
    if (stehtJetzt) {
      stehtJetzt = false;
      const durch = stufe === 2 ? "neu" : "pause";
      stufe = 0;
      melde("gerettet", durch);
    }
  };

  const handle = takt(pruefe, TAKT_PRUEF_MS);

  return {
    stop: beende,
    steht: () => stehtJetzt,
  };
}
